import type { Dispatch, FormEvent, SetStateAction } from 'react';
import type { Recipient } from '../../types';
import { useMemo, useCallback } from 'react';
import { Avatar, H6, IconButton, Stack } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { getCurrentUser, I18n } from '~/common/explicit-exports';
import { getColorsForAvatar, getLettersForAvatar } from '~/utils/avatar';
import { BrandedAvatarStack } from '~/features/common';
import {
  AvatarInputWrapper,
  ButtonWrapper,
  AvatarAndCloseButtonWrapper,
  Header,
  InfoText,
  VerticallyResizableTextarea,
} from './AskATeammate.styles';
import { getInfoWithTeammateNames } from './utils';

type Props = {
  message: string;
  setMessage: Dispatch<SetStateAction<string>>;
  recipients: Recipient[];
  onClose: () => void;
  onSend: (message: string) => void;
};

const TEAMMATE_LIMIT = 3;

const placeholder = I18n.t('js.learner.guide.task.ask_a_teammate.placeholder');
const sendLabel = I18n.t('js.learner.guide.task.ask_a_teammate.send');
const label = I18n.t('js.learner.guide.task.ask_a_teammate.label');
const closeLabel = I18n.t('common.application.close');
const submitButtonLabel = I18n.t('js.learner.guide.task.ask_a_teammate.submit_button_label');

export const AskATeammateForm = ({ message, setMessage, recipients, onSend, onClose }: Props) => {
  const currentUser = getCurrentUser();
  const theme = useTheme();
  const colors = getColorsForAvatar(theme, currentUser.enterprise.scheme_color);

  const info = useMemo(() => getInfoWithTeammateNames(recipients, TEAMMATE_LIMIT), [recipients]);

  const handleSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      if (!message.trim()) {
        return;
      }

      onSend(message);
      setMessage('');
      onClose();
    },
    [message, onClose, onSend, setMessage],
  );

  const avatars = useMemo(
    () =>
      recipients.map((recipient) => ({
        ...recipient,
        name: recipient.displayName,
        imgUrl: recipient.avatar,
      })),
    [recipients],
  );

  return (
    <form onSubmit={handleSubmit}>
      <Stack size={2}>
        <Header>
          <H6 size={2}>{label}</H6>
          <AvatarAndCloseButtonWrapper>
            <BrandedAvatarStack avatars={avatars} limit={TEAMMATE_LIMIT} />
            <IconButton variant="text" color="secondary" label={closeLabel} iconName="x:regular" onPress={onClose} />
          </AvatarAndCloseButtonWrapper>
        </Header>
        <InfoText density="lg" size={0} weight="regular">
          {info}
        </InfoText>
        <AvatarInputWrapper>
          <Avatar letters={getLettersForAvatar(currentUser.displayName)} imgUrl={currentUser.avatar} {...colors} />
          <VerticallyResizableTextarea
            label={null}
            errorMessage={null}
            placeholder={placeholder}
            aria-label={placeholder}
            rows={3}
            value={message}
            onChange={setMessage}
          />
        </AvatarInputWrapper>
        <ButtonWrapper>
          <IconButton
            type="submit"
            iconName="send:regular"
            label={sendLabel}
            isDisabled={!message.trim()}
            aria-label={submitButtonLabel}
          />
        </ButtonWrapper>
      </Stack>
    </form>
  );
};
